import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { finalize, forkJoin, of, switchMap, tap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {
  CollectDataPayload,
  CollectDataResponse
} from '../models/collect-data-payload';
import { TargetAudience } from '../models/target-audience';
import { TargetAudienceLocation } from '../models/target-audience-location';
import { CollectDataService } from '../services/collect-data.service';
import { UiStateService } from '../services/ui-state.service';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './home.component.html',
  styleUrl: './home.component.css'
})
export class HomeComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly collectDataService = inject(CollectDataService);
  private readonly router = inject(Router);
  private readonly uiState = inject(UiStateService);

  private readonly audienceForms = new Map<number, FormGroup>();
  private redirectAfterSubmit = false;

  readonly title = 'Digital Marketing Brief Collector';

  readonly typeOptions = [
    { label: 'Product', value: 'product' },
    { label: 'Service', value: 'service' },
    { label: 'Campaign', value: 'campaign' },
    { label: 'Event', value: 'event' }
  ];

  readonly collectForm = this.fb.nonNullable.group({
    type: ['', Validators.required],
    name: ['', [Validators.required, Validators.maxLength(150)]],
    business_type: ['', [Validators.required, Validators.maxLength(150)]],
    location: ['', [Validators.required, Validators.maxLength(120)]],
    description: [
      '',
      [Validators.required, Validators.minLength(20), Validators.maxLength(1500)]
    ]
  });

  protected readonly submittedResponse = signal<CollectDataResponse | null>(
    null
  );
  protected readonly errorMessage = signal<string>('');
  protected readonly successMessage = signal<string>('');
  protected readonly loading = signal<boolean>(false);

  protected readonly targetAudiences = signal<TargetAudience[]>([]);
  protected readonly audienceStatusMessage = signal<string>('');
  protected readonly audienceErrorMessage = signal<string>('');
  protected readonly audienceLoading = signal<boolean>(false);
  protected readonly editingAudienceId = signal<number | null>(null);

  protected readonly targetLocations = signal<TargetAudienceLocation[]>([]);
  protected readonly locationStatusMessage = signal<string>('');
  protected readonly locationErrorMessage = signal<string>('');
  protected readonly locationLoading = signal<boolean>(false);

  protected readonly companyGenerationPendingId = signal<number | null>(null);
  protected readonly companyGenerationMessage = signal<{
    locationId: number;
    text: string;
    isError: boolean;
  } | null>(null);
  protected readonly contactGenerationPendingId = signal<number | null>(null);
  protected readonly contactGenerationMessage = signal<{
    locationId: number;
    text: string;
    isError: boolean;
  } | null>(null);

  protected readonly responseId = computed(() => this.submittedResponse()?.id);

  protected readonly hasAudienceData = computed(
    () => this.targetAudiences().length > 0
  );

  protected readonly hasLocationData = computed(
    () => this.targetLocations().length > 0
  );

  get controls() {
    return this.collectForm.controls;
  }

  ngOnInit(): void {
    const cachedServiceId = this.uiState.serviceId();
    if (cachedServiceId) {
      this.restoreSummary(cachedServiceId);
    }
  }

  onSubmit(): void {
    if (this.collectForm.invalid) {
      this.collectForm.markAllAsTouched();
      this.errorMessage.set(
        'Please resolve the highlighted errors before submitting.'
      );
      return;
    }

    const payload: CollectDataPayload = this.collectForm.getRawValue();
    this.redirectAfterSubmit = true;
    this.loading.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');
    this.submittedResponse.set(null);

    this.targetAudiences.set([]);
    this.audienceStatusMessage.set('');
    this.audienceErrorMessage.set('');
    this.audienceLoading.set(false);
    this.editingAudienceId.set(null);
    this.audienceForms.clear();

    this.targetLocations.set([]);
    this.locationStatusMessage.set('');
    this.locationErrorMessage.set('');
    this.locationLoading.set(false);

    this.companyGenerationPendingId.set(null);
    this.companyGenerationMessage.set(null);
    this.contactGenerationPendingId.set(null);
    this.contactGenerationMessage.set(null);

    this.collectDataService
      .submit(payload)
      .pipe(
        tap(() => {
          this.successMessage.set('Saved successfully. Verifying record...');
        }),
        switchMap((response) =>
          this.collectDataService.fetchById(response.id).pipe(
            tap((record) => {
              this.submittedResponse.set(record);
              this.uiState.setServiceId(record.id);
              this.successMessage.set(
                `Record saved and verified (ID: ${record.id}). Preparing target audiences...`
              );
              this.collectForm.patchValue(
                {
                  type: record.type ?? '',
                  name: record.name ?? '',
                  business_type: record.business_type ?? '',
                  location: record.location ?? '',
                  description: record.description ?? ''
                },
                { emitEvent: false }
              );
              this.collectForm.markAsPristine();
            }),
            switchMap((record) => {
              this.audienceLoading.set(true);
              this.audienceErrorMessage.set('');
              this.audienceStatusMessage.set(
                'Analyzing service insights. This may take a moment...'
              );

              return this.collectDataService.analyzeData(record.id).pipe(
                tap(() => {
                  this.audienceStatusMessage.set(
                    'Analysis complete. Generating target audience profiles...'
                  );
                }),
                switchMap(() =>
                  this.collectDataService.generateTargetAudience(record.id)
                ),
                switchMap((result) => {
                  if (!result?.success) {
                    const message =
                      result?.message ||
                      'Target audience generation failed. Please try again.';
                    return throwError(() => new Error(message));
                  }

                  if (result?.message) {
                    this.audienceStatusMessage.set(result.message);
                  }

                  return this.collectDataService.getTargetAudiences(record.id);
                }),
                tap((audiences) => {
                  this.targetAudiences.set(audiences);
                  this.refreshAudienceForms(audiences);

                  this.successMessage.set(
                    `Record saved and verified (ID: ${record.id}).`
                  );
                  this.audienceStatusMessage.set(
                    audiences.length
                      ? `Loaded ${audiences.length} target audience profile(s).`
                      : 'No target audience profiles were returned.'
                  );

                  if (this.redirectAfterSubmit) {
                    this.redirectAfterSubmit = false;
                    this.router.navigate(['/audiences', record.id]);
                  }
                }),
                finalize(() => {
                  this.audienceLoading.set(false);
                })
              );
            })
          )
        ),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        error: (error) => {
          const message =
            error?.error?.detail ||
            error?.message ||
            'Unable to complete the request. Please try again.';
          this.errorMessage.set(message);
          this.audienceErrorMessage.set(message);
          this.successMessage.set('');
          this.audienceStatusMessage.set('');
          this.audienceLoading.set(false);
          this.redirectAfterSubmit = false;
        }
      });
  }

  resetForm(): void {
    this.collectForm.reset();
    this.loading.set(false);
    this.errorMessage.set('');
    this.successMessage.set('');
    this.submittedResponse.set(null);
    this.targetAudiences.set([]);
    this.audienceStatusMessage.set('');
    this.audienceErrorMessage.set('');
    this.targetLocations.set([]);
    this.locationStatusMessage.set('');
    this.locationErrorMessage.set('');
    this.editingAudienceId.set(null);
    this.audienceForms.clear();
    this.uiState.reset();

    this.companyGenerationPendingId.set(null);
    this.companyGenerationMessage.set(null);
    this.contactGenerationPendingId.set(null);
    this.contactGenerationMessage.set(null);
  }

  protected getAudienceForm(audienceId: number): FormGroup {
    let form = this.audienceForms.get(audienceId);
    if (!form) {
      const audience = this.targetAudiences().find(
        (item: TargetAudience) => item.id === audienceId
      );
      if (!audience) {
        throw new Error('Audience entry not found.');
      }
      form = this.buildAudienceForm(audience);
      this.audienceForms.set(audienceId, form);
    }
    return form;
  }

  protected startEditing(audience: TargetAudience): void {
    this.editingAudienceId.set(audience.id);
    this.audienceForms.set(audience.id, this.buildAudienceForm(audience));
  }

  protected cancelEditing(): void {
    const editingId = this.editingAudienceId();
    if (editingId !== null) {
      this.audienceForms.delete(editingId);
    }
    this.editingAudienceId.set(null);
  }

  protected saveAudience(audienceId: number): void {
    const form = this.getAudienceForm(audienceId);
    if (form.invalid) {
      form.markAllAsTouched();
      this.audienceErrorMessage.set(
        'Please fix the highlighted fields before saving.'
      );
      return;
    }

    this.audienceErrorMessage.set('');
    this.audienceStatusMessage.set('Saving audience changes...');
    this.audienceLoading.set(true);

    this.collectDataService
      .updateTargetAudience(audienceId, form.getRawValue())
      .pipe(
        tap((updated) => {
          this.replaceAudience(updated);
          this.audienceStatusMessage.set('Audience updated successfully.');
          this.editingAudienceId.set(null);
          this.audienceForms.delete(audienceId);
        }),
        finalize(() => {
          this.audienceLoading.set(false);
        })
      )
      .subscribe({
        error: (error) => {
          const message =
            error?.error?.detail ||
            error?.message ||
            'Unable to update the audience entry. Please try again.';
          this.audienceErrorMessage.set(message);
          this.audienceStatusMessage.set('');
        }
      });
  }

  protected deleteAudience(audience: TargetAudience): void {
    const label = audience.target_audience || 'this audience';
    const confirmed = window.confirm(`Remove "${label}"?`);
    if (!confirmed) {
      return;
    }

    this.audienceErrorMessage.set('');
    this.audienceStatusMessage.set('Removing audience...');
    this.audienceLoading.set(true);

    this.collectDataService
      .deleteTargetAudience(audience.id)
      .pipe(
        tap(() => {
          this.targetAudiences.update((items: TargetAudience[]) =>
            items.filter(
              (item: TargetAudience) => item.id !== audience.id
            )
          );
          this.audienceStatusMessage.set('Audience removed successfully.');
          if (this.editingAudienceId() === audience.id) {
            this.editingAudienceId.set(null);
          }
          this.audienceForms.delete(audience.id);
        }),
        finalize(() => {
          this.audienceLoading.set(false);
        })
      )
      .subscribe({
        error: (error) => {
          const message =
            error?.error?.detail ||
            error?.message ||
            'Unable to remove the audience entry. Please try again.';
          this.audienceErrorMessage.set(message);
          this.audienceStatusMessage.set('');
        }
      });
  }

  protected generateLocations(): void {
    const serviceId = this.responseId();
    if (!serviceId) {
      this.locationErrorMessage.set(
        'Save a record first to generate target areas.'
      );
      return;
    }

    this.locationErrorMessage.set('');
    this.locationStatusMessage.set(
      'Generating target areas. This may take a moment...'
    );
    this.locationLoading.set(true);
    this.companyGenerationPendingId.set(null);
    this.companyGenerationMessage.set(null);
    this.contactGenerationPendingId.set(null);
    this.contactGenerationMessage.set(null);

    this.collectDataService
      .createTargetAudienceLocations(serviceId)
      .pipe(
        switchMap((result) => {
          if (result.status?.toLowerCase() !== 'success') {
            const message =
              result.message || 'Target area generation failed. Try again.';
            return throwError(() => new Error(message));
          }
          return this.collectDataService.getLocationsByService(serviceId);
        }),
        finalize(() => {
          this.locationLoading.set(false);
        })
      )
      .subscribe({
        next: (locations) => {
          this.targetLocations.set(locations);
          this.locationStatusMessage.set(
            locations.length
              ? `Loaded ${locations.length} target area record(s).`
              : 'No target areas were returned.'
          );
          if (locations.length) {
            const storedLocationId = this.uiState.locationId();
            const matched =
              storedLocationId != null
                ? locations.find(
                    (item: TargetAudienceLocation) =>
                      item.id === storedLocationId
                  )
                : undefined;
            this.uiState.setLocationId(matched?.id ?? locations[0]?.id ?? null);
          }
          if (
            this.companyGenerationMessage() &&
            !locations.some(
              (item: TargetAudienceLocation) =>
                item.id === this.companyGenerationMessage()!.locationId
            )
          ) {
            this.companyGenerationMessage.set(null);
          }
        },
        error: (error) => {
          const message =
            error?.error?.detail ||
            error?.message ||
            'Unable to generate target areas. Please try again.';
          this.locationErrorMessage.set(message);
          this.locationStatusMessage.set('');
        }
      });
  }

  protected loadLocations(): void {
    const serviceId = this.responseId();
    if (!serviceId) {
      this.locationErrorMessage.set(
        'Save a record first to load target areas.'
      );
      return;
    }

    this.locationErrorMessage.set('');
    this.locationStatusMessage.set('Loading target areas...');
    this.locationLoading.set(true);
    this.companyGenerationPendingId.set(null);
    this.companyGenerationMessage.set(null);
    this.contactGenerationPendingId.set(null);
    this.contactGenerationMessage.set(null);

    this.collectDataService
      .getLocationsByService(serviceId)
      .pipe(
        finalize(() => {
          this.locationLoading.set(false);
        })
      )
      .subscribe({
        next: (locations) => {
          this.targetLocations.set(locations);
          this.locationStatusMessage.set(
            locations.length
              ? `Loaded ${locations.length} target area record(s).`
              : 'No target areas available yet.'
          );
          if (locations.length) {
            const storedLocationId = this.uiState.locationId();
            const matched =
              storedLocationId != null
                ? locations.find(
                    (item: TargetAudienceLocation) =>
                      item.id === storedLocationId
                  )
                : undefined;
            this.uiState.setLocationId(matched?.id ?? locations[0]?.id ?? null);
          }
          if (
            this.companyGenerationMessage() &&
            !locations.some(
              (item: TargetAudienceLocation) =>
                item.id === this.companyGenerationMessage()!.locationId
            )
          ) {
            this.companyGenerationMessage.set(null);
          }
        },
        error: (error) => {
          const message =
            error?.error?.detail ||
            error?.message ||
            'Unable to load target areas. Please try again.';
          this.locationErrorMessage.set(message);
          this.locationStatusMessage.set('');
        }
      });
  }

  protected generateTargetCompany(location: TargetAudienceLocation): void {
    const serviceId = this.responseId();
    if (!serviceId) {
      this.locationErrorMessage.set(
        'Save a record first to generate company information.'
      );
      return;
    }

    this.companyGenerationPendingId.set(location.id);
    this.companyGenerationMessage.set(null);
    this.locationErrorMessage.set('');

    this.collectDataService
      .generateTargetCompany(serviceId, location.id)
      .pipe(
        finalize(() => {
          this.companyGenerationPendingId.set(null);
        })
      )
      .subscribe({
        next: (result) => {
          const isSuccess =
            result?.status?.toLowerCase() === 'success' ||
            result?.success === true;
          if (!isSuccess) {
            const message =
              result?.message ||
              'Company generation did not complete. Please try again.';
            this.companyGenerationMessage.set({
              locationId: location.id,
              text: message,
              isError: true
            });
            return;
          }

          this.companyGenerationMessage.set({
            locationId: location.id,
            text:
              result?.message ||
              'Company information generated successfully. Use View Information to load results.',
            isError: false
          });
        },
        error: (error) => {
          const message =
            error?.error?.detail ||
            error?.message ||
            'Unable to generate company information. Please try again.';
          this.companyGenerationMessage.set({
            locationId: location.id,
            text: message,
            isError: true
          });
        }
      });
  }

  protected viewCompanyInformation(location: TargetAudienceLocation): void {
    const serviceId = this.responseId();
    if (!serviceId) {
      this.locationErrorMessage.set(
        'Save a record first to view company information.'
      );
      return;
    }

    this.uiState.setServiceId(serviceId);
    this.uiState.setLocationId(location.id);
    this.router.navigate(['/companies', serviceId, location.id]);
  }

  protected generateCompanyContacts(location: TargetAudienceLocation): void {
    const serviceId = this.responseId();
    if (!serviceId) {
      this.locationErrorMessage.set(
        'Save a record first to generate company contact details.'
      );
      return;
    }

    this.contactGenerationPendingId.set(location.id);
    this.contactGenerationMessage.set(null);
    this.locationErrorMessage.set('');

    this.collectDataService
      .generateCompanyContactDetails(serviceId, location.id)
      .pipe(
        finalize(() => {
          this.contactGenerationPendingId.set(null);
        })
      )
      .subscribe({
        next: (result) => {
          const isSuccess =
            result?.status?.toLowerCase() === 'success' ||
            result?.success === true;
          if (!isSuccess) {
            const message =
              result?.message ||
              'Company contact generation did not complete. Please try again.';
            this.contactGenerationMessage.set({
              locationId: location.id,
              text: message,
              isError: true
            });
            return;
          }

          this.contactGenerationMessage.set({
            locationId: location.id,
            text:
              result?.message ||
              'Company contact details generated successfully. Use View Company Contacts to review.',
            isError: false
          });
        },
        error: (error) => {
          const message =
            error?.error?.detail ||
            error?.message ||
            'Unable to generate company contact details. Please try again.';
          this.contactGenerationMessage.set({
            locationId: location.id,
            text: message,
            isError: true
          });
        }
      });
  }

  protected viewCompanyContacts(location: TargetAudienceLocation): void {
    const serviceId = this.responseId();
    if (!serviceId) {
      this.locationErrorMessage.set(
        'Save a record first to view company contact details.'
      );
      return;
    }

    this.uiState.setServiceId(serviceId);
    this.uiState.setLocationId(location.id);
    this.router.navigate(['/contacts', serviceId, location.id]);
  }

  protected goToAudiences(): void {
    const serviceId = this.responseId() ?? this.uiState.serviceId();
    if (!serviceId) {
      this.audienceErrorMessage.set(
        'Save a record first to view target audience profiles.'
      );
      return;
    }

    this.router.navigate(['/audiences', serviceId]);
  }

  protected goToStoredCompanies(): void {
    const serviceId = this.responseId() ?? this.uiState.serviceId();
    if (!serviceId) {
      this.locationErrorMessage.set(
        'Save and restore a record before viewing companies.'
      );
      return;
    }

    let targetLocation: TargetAudienceLocation | undefined;
    const storedLocationId = this.uiState.locationId();
    if (storedLocationId != null) {
      targetLocation = this.targetLocations().find(
        (item: TargetAudienceLocation) => item.id === storedLocationId
      );
    }
    if (!targetLocation) {
      targetLocation = this.targetLocations()[0];
    }
    if (!targetLocation) {
      this.locationErrorMessage.set(
        'Generate target areas before proceeding to company insights.'
      );
      return;
    }

    this.viewCompanyInformation(targetLocation);
  }

  private restoreSummary(serviceId: number): void {
    this.loading.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');
    this.uiState.setServiceId(serviceId);

    this.collectDataService
      .fetchById(serviceId)
      .pipe(
        tap((record) => {
          this.submittedResponse.set(record);
          this.successMessage.set(`Restored record (ID: ${record.id}).`);
          this.collectForm.patchValue(
            {
              type: record.type ?? '',
              name: record.name ?? '',
              business_type: record.business_type ?? '',
              location: record.location ?? '',
              description: record.description ?? ''
            },
            { emitEvent: false }
          );
        }),
        switchMap((record) =>
          forkJoin({
            audiences: this.collectDataService
              .getTargetAudiences(record.id)
              .pipe(catchError(() => of([] as TargetAudience[]))),
            locations: this.collectDataService
              .getLocationsByService(record.id)
              .pipe(catchError(() => of([] as TargetAudienceLocation[])))
          })
        ),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: ({ audiences, locations }) => {
          this.targetAudiences.set(audiences);
          this.refreshAudienceForms(audiences);
          this.audienceStatusMessage.set(
            audiences.length
              ? `Loaded ${audiences.length} target audience profile(s).`
              : 'No target audience profiles available yet.'
          );
          this.targetLocations.set(locations);
          this.locationStatusMessage.set(
            locations.length
              ? `Loaded ${locations.length} target area record(s).`
              : 'No target areas available yet.'
          );

          if (locations.length) {
            const storedLocationId = this.uiState.locationId();
          const matched =
            storedLocationId != null
              ? locations.find(
                  (item: TargetAudienceLocation) => item.id === storedLocationId
                )
              : undefined;
            this.uiState.setLocationId(matched?.id ?? locations[0]?.id ?? null);
          }
        },
        error: (error) => {
          const message =
            error?.error?.detail ||
            error?.message ||
            'Unable to restore the saved data. Please resubmit the form.';
          this.errorMessage.set(message);
        }
      });
  }

  protected renderArray(value: unknown): string[] {
    if (value == null) {
      return [];
    }

    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (item == null) {
            return '';
          }
          if (typeof item === 'string') {
            return item.trim();
          }
          if (typeof item === 'number' || typeof item === 'boolean') {
            return String(item);
          }
          if (item instanceof Date) {
            return item.toISOString();
          }
          return JSON.stringify(item);
        })
        .filter(Boolean);
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return [];
      }

      const parsed = this.tryParseJson(trimmed);
      if (Array.isArray(parsed)) {
        return this.renderArray(parsed);
      }

      return [trimmed];
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return [String(value)];
    }

    return [];
  }

  protected renderText(value: unknown): string {
    const tokens = this.renderArray(value);
    if (tokens.length) {
      return tokens.join(', ');
    }

    if (value && typeof value === 'object') {
      const summary = this.renderObjectSummary(value);
      return summary.length ? summary.join(' | ') : 'N/A';
    }

    return 'N/A';
  }

  protected renderObjectSummary(value: unknown): string[] {
    if (value == null) {
      return [];
    }

    let source = value;

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return [];
      }
      const parsed = this.tryParseJson(trimmed);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        source = parsed;
      } else {
        return [trimmed];
      }
    }

    if (Array.isArray(source)) {
      return this.renderArray(source);
    }

    if (typeof source !== 'object') {
      return this.renderArray(source);
    }

    return Object.entries(source as Record<string, unknown>).map(
      ([key, val]) => {
        const values = this.renderArray(val);
        const content =
          values.length || (val && typeof val === 'object')
            ? values.length
              ? values.join(', ')
              : JSON.stringify(val)
            : 'N/A';
        return `${this.formatKey(key)}: ${content}`;
      }
    );
  }

  private refreshAudienceForms(audiences: TargetAudience[]): void {
    this.audienceForms.clear();
    audiences.forEach((audience) => {
      this.audienceForms.set(audience.id, this.buildAudienceForm(audience));
    });
  }

  private replaceAudience(updated: TargetAudience): void {
    this.targetAudiences.update((items: TargetAudience[]) =>
      items.map((item: TargetAudience) =>
        item.id === updated.id ? { ...item, ...updated } : item
      )
    );
  }

  private buildAudienceForm(audience: TargetAudience): FormGroup {
    return this.fb.group({
      target_audience: [
        audience.target_audience ?? '',
        [Validators.required, Validators.maxLength(200)]
      ],
      audience_type: [audience.audience_type ?? '', Validators.maxLength(120)],
      market_type: [audience.market_type ?? '', Validators.maxLength(150)],
      company_size: [audience.company_size ?? '', Validators.maxLength(150)],
      industries: [audience.industries ?? '', Validators.maxLength(200)],
      decision_makers: [
        audience.decision_makers ?? '',
        Validators.maxLength(200)
      ],
      locations: [audience.locations ?? '', Validators.maxLength(200)],
      psychographics: [
        audience.psychographics ?? '',
        Validators.maxLength(2000)
      ],
      behavior: [audience.behavior ?? '', Validators.maxLength(2000)],
      needs: [audience.needs ?? '', Validators.maxLength(2000)],
      market_size_estimate: [
        audience.market_size_estimate ?? '',
        Validators.maxLength(150)
      ],
      priority_level: [
        audience.priority_level ?? '',
        Validators.maxLength(150)
      ],
      market_insights: [
        audience.market_insights ?? '',
        Validators.maxLength(2500)
      ],
      recommendations: [
        audience.recommendations ?? '',
        Validators.maxLength(2500)
      ]
    });
  }

  private tryParseJson(value: string): unknown | null {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  private formatKey(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  protected isCompanyGenerating(locationId: number): boolean {
    return this.companyGenerationPendingId() === locationId;
  }

  protected isContactGenerating(locationId: number): boolean {
    return this.contactGenerationPendingId() === locationId;
  }
}
